import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import * as _ from 'lodash'

import { AppLayoutMenuItems } from './AppLayoutMenuItems'
import { OrgAppLayoutMenubar } from './OrgAppLayoutMenubar'

/**
 * AppLayoutMenubar component renders a sidebar menu with navigation links
 * filtered and grouped based on user roles and permissions.
 *
 * @module appLayout/AppLayoutMenubar
 * @description AppLayoutMenubar component renders a sidebar menu with navigation links
 * filtered and grouped based on user roles and permissions.
 * @example
 * const envData = { REACT_APP_THEME_LOGO: '/path/to/logo.png' };
 * const toggleMenu = true;
 * const setToggleMenu = () => {};
 * const sideMenuRef = React.createRef();
 * const authDetails = {
 *   session: { pages: [{ pageRoles: [{ roleName: 'admin' }] }] },
 *   role: 'admin',
 * };
 * const routesData = [
 *   {
 *     isPrivate: 1,
 *     pageName: 'Dashboard',
 *     apiEndPointSchema: { group: 'Main', noMenu: false },
 *     pageTitle: 'Dashboard',
 *   },
 *   // More route objects...
 * ];
 *
 * return (
 *   <AppLayoutMenubar
 *     toggleMenu={toggleMenu}
 *     setToggleMenu={setToggleMenu}
 *     sideMenuRef={sideMenuRef}
 *     envData={envData}
 *     authDetails={authDetails}
 *     routesData={routesData}
 *   />
 * );
 *
 * @param {Object} envData - Environment-specific data
 * @param {string} envData.REACT_APP_THEME_LOGO - URL of the logo to display
 * @param {boolean} toggleMenu - Boolean that determines if the menu is toggled
 * @param {function} setToggleMenu - Function to toggle the menu visibility
 * @param {Object} sideMenuRef - React ref object for the sidebar element
 * @param {Object} authDetails - Authentication details of the current user
 * @param {Object} authDetails.session - Current session details
 * @param {Array} authDetails.session.pages - Array of page objects accessible to the user
 * @param {string} authDetails.role - Role of the current user
 * @param {Array} routesData - Array of route objects
 * @param {number} routesData[].isPrivate - Indicates if the route is private (1) or public (0)
 * @param {string} routesData[].pageName - Name of the page
 * @param {string} routesData[].pageTitle - Title of the page
 * @param {Object} routesData[].apiEndPointSchema - Schema containing endpoint details
 * @param {string} routesData[].apiEndPointSchema.group - Group name to which the route belongs
 * @param {boolean} routesData[].apiEndPointSchema.noMenu - Indicates if the route should be hidden from the menu
 *
 * @returns {React.Element} The rendered sidebar menu component.
 */
export const AppLayoutMenubar = ({
  toggleMenu,
  sideMenuRef,
  envData,
  authDetails,
  routesData,
  setToggleMenu,
  activeOrgRoutes,
  activeOrg,
  unsetOrg,
}) => {
  const activeMenuGroup = sessionStorage.getItem('menuGroupActive')
    ? JSON.parse(sessionStorage.getItem('menuGroupActive'))
    : null
  const [toggleActiveOrg, setToggleActiveOrg] = React.useState(false)
  const getMainMenu = sessionStorage.getItem('mainMenu')
  const [toggleMainMenu, setToggleMainMenu] = React.useState(getMainMenu ? true : false)
  const [activeGroup, setActiveGroup] = React.useState(activeMenuGroup)
  const menus = routesData.filter((f) => f.isPrivate === 1 || f.isPrivate === true)

  let menu = []
  authDetails?.session?.pages.forEach((p) =>
    p.pageRoles.filter((f) => f.roleName === authDetails.role && menu.push(p)),
  )
  let userMenu = []
  menu.forEach((m) => menus.forEach((mn) => mn.pageName === m.pageName && userMenu.push(mn)))
  const parsedMenu = []
  userMenu.map((m) => {
    const parsed = JSON.parse(m.apiEndPointSchema)
    parsedMenu.push({ ...m, apiEndPointSchema: parsed })
  })

  const userMenuGrouped = _.groupBy(parsedMenu, (g) => g.apiEndPointSchema.group)

  const urlPath = window.location.pathname
  const urlLink = urlPath.split('/console/')

  const activateMenuGroup = (group, link) => {
    sessionStorage.setItem('menuGroupActive', group)
    sessionStorage.setItem('menuGroupActiveLink', link)
    setActiveGroup(group)
  }
  const deActivateMenuGroup = () => {
    sessionStorage.removeItem('menuGroupActive')
    sessionStorage.removeItem('menuGroupActiveLink')
  }

  const mainMenuToggle = (option) => {
    if (option) {
      sessionStorage.setItem('mainMenu', true)
    } else {
      sessionStorage.removeItem('mainMenu')
    }
    setToggleMainMenu(option)
  }

  React.useEffect(() => {
    if (activeOrg !== null) {
      mainMenuToggle(true)
    }
  }, [])

  return (
    <div className="trtui-app-menu trtui-navbar-menu" style={{ marginLeft: toggleMenu ? 0 : '' }}>
      <div
        className="trtui-navbar-brand-box trtui-d-block"
        style={{ borderBottom: '1px solid var(--vz-header-border)' }}
      >
        <Link
          to="/"
          className="trtui-logo trtui-logo-dark"
          style={{ lineHeight: toggleMenu ? '60px' : '70px' }}
        >
          <span className="trtui-logo-lg trtui-d-block">
            <img src={envData.REACT_APP_THEME_LOGO} alt="logo-lg-dark" />
          </span>
        </Link>
        <Link
          to="/"
          className="trtui-logo trtui-logo-light"
          style={{ lineHeight: toggleMenu ? '60px' : '70px' }}
        >
          <span className="trtui-logo-lg trtui-d-block">
            <img src={envData.REACT_APP_THEME_LOGO} alt="logo-lg-light" />
          </span>
        </Link>
        <button
          type="button"
          className="trtui-btn trtui-btn-sm trtui-p-0 trtui-fs-20 trtui-header-item trtui-float-end trtui-btn-vertical-sm-hover"
          id="vertical-hover"
        >
          <i className="ri-record-circle-line" />
        </button>
      </div>
      <div
        data-simplebar="init"
        className="trtui-h-100 trtui-simplebar-scrollable-y"
        ref={sideMenuRef}
      >
        <div className="trtui-simplebar-wrapper">
          <div className="trtui-simplebar-height-auto-observer-wrapper">
            <div className="trtui-simplebar-height-auto-observer"></div>
          </div>
          <div className="trtui-simplebar-mask">
            <div className="trtui-simplebar-offset">
              <div className="trtui-simplebar-content-wrapper">
                <div className="trtui-simplebar-content">
                  <div
                    className="trtui-container-fluid cScrollBar"
                    style={{ maxHeight: '100vh', paddingBottom: '8rem' }}
                  >
                    <div id="two-column-menu" />
                    <ul className="trtui-navbar-nav" data-simplebar="init" id="navbar-nav">
                      {envData.REACT_APP_APP_ISONLINE === '1' && (
                        <>
                          <li
                            className="trtui-menu-title"
                            onClick={() => mainMenuToggle(!toggleMainMenu)}
                          >
                            <span
                              className={`trtui-nav-link trtui-menu-link trtui-cursor-pointer ${toggleMainMenu ? 'trtui-active' : ''}`}
                              style={{
                                fontSize: '.8375rem',
                                textTransform: 'uppercase',
                              }}
                            >
                              Menu{' '}
                              <i
                                className={`${toggleMainMenu ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}`}
                                style={{ position: 'absolute', right: '0px' }}
                              />
                            </span>
                          </li>

                          <div style={{ display: toggleMainMenu ? 'none' : 'block' }}>
                            {Object.keys(userMenuGrouped)
                              .sort((a, b) => a.localeCompare(b))
                              .map((grp, grpInd) =>
                                grp === 'undefined' ? (
                                  <React.Fragment key={grpInd}>
                                    {Object.keys(userMenuGrouped)
                                      .sort((a, b) => a.localeCompare(b))
                                      .map((grp, grpInd) => (
                                        <React.Fragment key={grpInd}>
                                          {userMenuGrouped[grp]
                                            .sort((a, b) =>
                                              a['pageTitle'].localeCompare(b['pageTitle']),
                                            )
                                            .map(
                                              (menu, menuIndex) =>
                                                grp === 'undefined' && (
                                                  <React.Fragment key={menuIndex}>
                                                    {!menu.apiEndPointSchema.noMenu && (
                                                      <AppLayoutMenuItems
                                                        key={menuIndex}
                                                        setToggleMenu={setToggleMenu}
                                                        menuItem={menu}
                                                        urlLink={urlLink[1]}
                                                        isUndefined={true}
                                                        deActivateMenuGroup={deActivateMenuGroup}
                                                      />
                                                    )}
                                                  </React.Fragment>
                                                ),
                                            )}
                                        </React.Fragment>
                                      ))}
                                  </React.Fragment>
                                ) : (
                                  <React.Fragment key={grpInd}>
                                    <li className="trtui-nav-item">
                                      <span
                                        className={`trtui-nav-link trtui-menu-link trtui-cursor-pointer ${activeGroup === grpInd ? 'trtui-active' : ''}`}
                                        onClick={() =>
                                          activateMenuGroup(
                                            activeGroup === grpInd ? null : grpInd,
                                            urlLink[1],
                                          )
                                        }
                                        style={{
                                          fontSize: '.8375rem',
                                          textTransform: 'uppercase',
                                        }}
                                      >
                                        {grp}{' '}
                                        <i
                                          className={`${activeGroup === grpInd ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`}
                                          style={{ position: 'absolute', right: '0px' }}
                                        />
                                      </span>
                                      <div
                                        className={`trtui-collapse trtui-menu-dropdown ${activeGroup === grpInd ? 'trtui-show' : ''}`}
                                        style={{
                                          WebkitTransition: 'all 0.6s linear 0.4s',
                                          msTransition: 'all 0.6s linear 0.4s',
                                          transition: 'all 0.6s linear 0.4s',
                                        }}
                                      >
                                        {userMenuGrouped[grp]
                                          .sort((a, b) =>
                                            a['pageTitle'].localeCompare(b['pageTitle']),
                                          )
                                          .map((menu, menuIndex) => (
                                            <React.Fragment key={menuIndex}>
                                              {!menu.apiEndPointSchema.noMenu && (
                                                <AppLayoutMenuItems
                                                  key={menuIndex}
                                                  setToggleMenu={setToggleMenu}
                                                  menuItem={menu}
                                                  urlLink={urlLink[1]}
                                                  grp={grp}
                                                />
                                              )}
                                            </React.Fragment>
                                          ))}
                                      </div>
                                    </li>
                                  </React.Fragment>
                                ),
                              )}
                            <li
                              data-key="dashboard"
                              className="trtui-nav-item"
                              style={{ paddingLeft: '0px' }}
                            >
                              <Link
                                className={`trtui-nav-link trtui-menu-link ${urlLink[1] === 'dashboard' ? 'trtui-active' : ''}`}
                                to="/console/dashboard"
                              >
                                <i data-testid="menuItemIcon" className="ri-bar-chart-box-line" />
                                <span>Dashboard</span>
                              </Link>
                            </li>
                          </div>
                        </>
                      )}
                      {activeOrgRoutes !== null && (
                        <>
                          {envData.REACT_APP_APP_ISONLINE === '1' && (
                            <div className="trtui-btn-group trtui-w-100">
                              <button
                                type="button"
                                className={`trtui-btn trtui-btn-primary trtui-dropdown-toggle ${toggleActiveOrg ? 'trtui-show' : ''}`}
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                style={{ textWrap: 'auto' }}
                                onClick={() => setToggleActiveOrg(!toggleActiveOrg)}
                              >
                                {activeOrg}
                              </button>
                              <div
                                className={`trtui-dropdown-menu ${toggleActiveOrg ? 'trtui-show' : ''}`}
                                style={{
                                  position: 'absolute',
                                  inset: '0px auto auto 0px',
                                  margin: '0px',
                                  transform: 'translate3d(0px, 40.8px, 0px)',
                                }}
                              >
                                <a className="trtui-dropdown-item" href="#" onClick={unsetOrg}>
                                  Exit
                                </a>
                              </div>
                            </div>
                          )}
                          {envData.REACT_APP_APP_ISONLINE === '0' && (
                            <li className="trtui-menu-title">
                              <span data-key="t-menu">Menu</span>
                            </li>
                          )}
                          {activeOrgRoutes.formData?.orgRoles?.length > 0 && (
                            <OrgAppLayoutMenubar
                              envData={envData}
                              toggleMenu={toggleMenu}
                              setToggleMenu={setToggleMenu}
                              sideMenuRef={sideMenuRef}
                              authDetails={authDetails}
                              activeOrgRoutes={activeOrgRoutes}
                            />
                          )}
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="trtui-sidebar-background" />
    </div>
  )
}

AppLayoutMenubar.propTypes = {
  envData: PropTypes.object.isRequired,
  toggleMenu: PropTypes.bool.isRequired,
  setToggleMenu: PropTypes.func.isRequired,
  sideMenuRef: PropTypes.object.isRequired,
  authDetails: PropTypes.object.isRequired,
  routesData: PropTypes.array.isRequired,
  activeOrg: PropTypes.string,
  unsetOrg: PropTypes.func,
  activeOrgRoutes: PropTypes.object,
}
